import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useIDBKeyval } from '@vueuse/integrations/useIDBKeyval'
import { http } from '@/helpers/http'
import { useUserStore } from '@/stores/user'
import { useProjectsStore } from '@/stores/projects'
import { useTagsStore } from '@/stores/tags'
import { Products } from '@/helpers/product'
import { matchesTemplatePlan, orderTemplates, toTemplatePlan, toTemplateSort } from '@/helpers/templateSort'
import type { TDropdownOption } from '@/types/dropdown'
import type {
  TTemplateList,
  TPageSize,
  TTemplate,
  TTemplateThemeList,
  TResponse,
  TTemplateCover,
  TTemplateCoverResponse,
} from '@/types/templates'
import type { TProject, TProjectCreateResult } from '@/types/projects'
import type { TLastTagImageUpdate, TLastTagImageUpdateList } from '@/types/tags'
import type { TAmazonTemplateImageElement } from '@/types/amazon-cover'

export const useTemplatesStore = defineStore('templates', () => {
  const templateList = ref<TTemplateList>([])
  const proTemplateLabel: string = 'PRO_TEMPLATE'
  const users = useUserStore()
  const tags = useTagsStore()
  const projects = useProjectsStore()
  const templateToReview = ref<TTemplate | null>(null)
  const templatesCovers = ref<{ [templateId: string]: TTemplateCover[] }>({})
  const templateImageElements = ref<TAmazonTemplateImageElement[]>([])
  const { data: dataUpdatesIDB } = useIDBKeyval<TLastTagImageUpdateList>('lastTagImageUpdates', [])
  const { data: dataTemplatesIDB, set: setTemplatesIDB } = useIDBKeyval<TTemplateList>('templates', [])
  const { data: dataTemplatesAccountIDB, set: setTemplatesAccountIDB } = useIDBKeyval<number>('templatesAccountType', 0)

  const pageOrientations: TDropdownOption[] = [
    {
      name: 'Portrait',
      label: 'Portrait',
    },
    {
      name: 'Landscape',
      label: 'Landscape',
    },
  ]
  const pageOrientation = ref<TDropdownOption>(pageOrientations[0]!)
  const allPageSizes: Readonly<TPageSize[]> = [
    { name: 'Letter', width: 816, height: 1056, landscape: false },
    { name: 'A4', width: 794, height: 1123, landscape: false },
    { name: 'A5', width: 558, height: 794, landscape: false },
    { name: '6x9', width: 576, height: 864, landscape: false },
    { name: 'Legal', width: 816, height: 1344, landscape: false },
    { name: 'A3', width: 1124, height: 1588, landscape: false },
    { name: 'Square', width: 1080, height: 1080, landscape: false },
  ]
  const allPrintBookPageSizes: Readonly<TPageSize[]> = [
    { name: '5.5x8.5', width: 528, height: 816, landscape: false },
    { name: '6x9', width: 576, height: 864, landscape: false },
    { name: '6.14x9.21', width: 590, height: 884, landscape: false },
    { name: '7x10', width: 672, height: 960, landscape: false },
    { name: '8.25x11', width: 792, height: 1056, landscape: false },
  ]
  const regularPageSizes: TDropdownOption[] = allPageSizes.map((e: TPageSize) => ({ label: e.name, name: e.name }))
  const printBookPageSizes: TDropdownOption[] = allPrintBookPageSizes.map((e: TPageSize) => ({
    label: e.name,
    name: e.name,
  }))
  const pageSizes = ref<TDropdownOption[]>(regularPageSizes)
  const pageSize = ref<TDropdownOption>(regularPageSizes[0]!)

  const ratio = computed(() => {
    const allSizes = [...allPageSizes, ...allPrintBookPageSizes]
    const size = allSizes.find((p) => p.name === pageSize.value.name)

    if (size) {
      const [width = 1, height = 1] = [size.height, size.width].sort((a, b) => a - b)

      if (pageOrientation.value.name === 'Landscape') {
        return height / width
      } else {
        return width / height
      }
    } else {
      return 2 / 3
    }
  })

  async function fetchTemplateImageElements(projectId: number): Promise<void> {
    try {
      const res = await http.get<TAmazonTemplateImageElement[] | null>('/get-template-image-elements/' + projectId)
      if (!res) throw new Error('Failed to fetch fetch template image elements')
      templateImageElements.value = res
    } catch {
      templateImageElements.value = []
    }
  }

  async function getDefaultCCTemplateOrFirstProTemplate(): Promise<TTemplate | null> {
    try {
      const res = await http.get<{ status: string; template: TTemplate | null }>(
        '/get-default-cc-template-or-first-pro-template/',
      )
      if (!res?.template) throw new Error('Failed to fetch default CC template or first pro template')
      return res.template
    } catch {
      return null
    }
  }

  async function getDefaultCCTemplateId(): Promise<number | null> {
    try {
      const res = await http.get<{ status: string; id: number | null }>('/default-cc-template-id/')

      return res?.id ?? null
    } catch {
      return null
    }
  }

  function isProTemplate(template: TTemplate): boolean {
    return template.template_type === proTemplateLabel
  }

  function filterTemplateList(
    themeOption: TDropdownOption[],
    searchString: string,
    planOption?: TDropdownOption,
    sortOption?: TDropdownOption,
  ): TTemplateList {
    const plan = toTemplatePlan(planOption?.name)
    const searched = searchString.toLocaleLowerCase()
    const filteredTemplateList = templateList.value.filter(
      (template) =>
        template.name.toLocaleLowerCase().includes(searched) && matchesTemplatePlan(template, plan, isProTemplate),
    )

    return orderTemplates(filteredTemplateList, {
      sort: toTemplateSort(sortOption?.name),
      themeIds: themeOption.map((theme) => Number(theme.name)),
      mixLocked: users.user.account_type === Products.Standard,
      isLocked: isProTemplate,
    })
  }

  async function checkIdbNotActual(): Promise<boolean> {
    //no info about last updates in IndexedDB or no template data stored in IndexedDB at all
    if (!dataTemplatesIDB.value?.length || !dataUpdatesIDB.value?.length) {
      return true
    }

    if (dataTemplatesAccountIDB.value !== users.user.account_type) {
      return true
    }

    if (typeof dataUpdatesIDB.value === 'string') {
      await tags.fetchLastTagImageUpdates(true)
    }

    const lastUpdateDB: TLastTagImageUpdate | null = tags.getLastUpdateEntryByTagName(
      tags.lastTagImageUpdates,
      'templates',
    )

    let lastUpdateIDB: TLastTagImageUpdate | null =
      typeof dataUpdatesIDB.value !== 'string'
        ? tags.getLastUpdateEntryByTagName(dataUpdatesIDB.value, 'templates')
        : null

    //update missing template field in IDB, first usage for all users
    if (!lastUpdateIDB) {
      await tags.fetchLastTagImageUpdates(true)
      lastUpdateIDB =
        typeof dataUpdatesIDB.value !== 'string'
          ? tags.getLastUpdateEntryByTagName(dataUpdatesIDB.value, 'templates')
          : null
    }

    return tags.isIDBNotActual(lastUpdateIDB, lastUpdateDB)
  }

  async function initTemplateList() {
    const idbActual = !(await checkIdbNotActual())

    if (idbActual) {
      templateList.value = dataTemplatesIDB.value
    } else {
      const templatesDB: TTemplateList | null = await http.get<TTemplateList>('/pr-templates/0/1')
      templateList.value = templatesDB ?? []
      await setTemplatesIDB(templateList.value)
      await setTemplatesAccountIDB(users.user.account_type)
      await tags.actualizeIDBData(tags.getLastUpdateEntryByTagName(tags.lastTagImageUpdates, 'templates'), 'templates')
    }
  }

  async function getTemplateThemes(): Promise<TTemplateThemeList> {
    const themes: TTemplateThemeList | null = await http.post<TTemplateThemeList>('/tags/hints', {})
    return themes ?? []
  }

  async function deleteTemplate(template: TTemplate): Promise<void> {
    await http.delete('/pr-templates/' + template.id, true)
    const index = templateList.value.indexOf(template)
    if (index !== -1) {
      templateList.value.splice(index, 1)
    }
  }

  function templatesByTags(tags: number[], excludeIds: number[]): TTemplate[] {
    return templateList.value
      .filter((template) => tags.some((tag) => template.tags.includes(tag)))
      .filter((template) => !excludeIds.includes(template.id))
  }

  async function updatePublishedTemplate(projectId: number): Promise<TResponse> {
    const result: TResponse | null = await http.post<TResponse>('/update_published_template/', { projectId })
    if (!result || result?.status !== 'success') {
      return {
        status: 'error',
        message: result?.message ?? 'Error updating template',
      }
    }

    return result
  }

  async function unpublishTemplate(projectId: number): Promise<TResponse> {
    const result: TResponse | null = await http.post<TResponse>('/unpublish_template/', { projectId })
    if (!result || result?.status !== 'success') {
      return {
        status: 'error',
        message: result?.message ?? 'Error unpublishing template',
      }
    }

    return result
  }

  async function duplicateTemplate(
    oldTemplateName: string,
    newTemplateName: string,
    templateId: number,
    userReceiverEmail?: string,
    userReceiverId?: number,
  ): Promise<TResponse> {
    const result: TResponse | null = await http.post<TResponse>('/duplicate_template/', {
      oldTemplateName,
      newTemplateName,
      templateId,
      userReceiverEmail: userReceiverEmail ?? '',
      userReceiverId: userReceiverId ?? 0,
    })
    if (!result || result?.status !== 'success') {
      return {
        status: 'error',
        message: result?.message ?? 'Error duplicating template',
      }
    }

    return result
  }

  async function publishTemplate(projectId: number): Promise<TResponse> {
    try {
      const result: TResponse | null = await http.post<TResponse>('/publish_template/', { projectId })
      if (!result || result?.status !== 'success') {
        return {
          status: 'error',
          message: result?.message ?? 'Error publishing template',
        }
      }
      return result
    } catch {
      return {
        status: 'error',
        message: 'Error publishing template',
      }
    }
  }

  async function exportTemplate(projectId: number): Promise<boolean> {
    const result: { status: string; message: string } | null = await http.post('/export_pro_template', { projectId })
    return result?.status === 'success'
  }

  async function importTemplate(data: string): Promise<boolean> {
    const result: { status: string; message: string } | null = await http.post('/import_pro_template', { data })
    return result?.status === 'success'
  }

  async function removeTemporaryFile(userId: number, projectId: number): Promise<void> {
    await http.post('/remove_temporary_template_file', { userId, projectId }, true)
  }

  async function createNewTemplate(): Promise<boolean> {
    const template: TTemplate | null = await http.get('/pr-default-template-record/')
    if (!template) {
      return false
    }

    const projectName = 'Template ' + Math.round(new Date().getTime() / 1000)
    const payload = {
      name: projectName,
      type: 'ebook',
      template: template.id,
      templateName: template.name,
      pageSize: 'A4',
      pageWidth: 816,
      pageHeight: 1056,
      templateType: true,
    }

    const projectCreated: TProjectCreateResult | null = await http.post('/projects', payload)
    if (!projectCreated) {
      return false
    }

    const project: TProject | null = await http.get('/projects/' + projectCreated.id)
    if (!project) {
      return false
    }

    const payloadT = {
      name: projectName,
      projectId: project.id,
      thumbnailLink: 'assets/images/projects/' + project.cover,
      html: project?.pages && project.pages[0] && project.pages[0].html ? project?.pages[0]?.html : '',
      css: project?.pages && project.pages[0] && project.pages[0].css ? project?.pages[0]?.css : '',
    }

    const newTemplate: TTemplate | null = await http.post('/templates-editor/template', payloadT)
    if (!newTemplate) {
      return false
    }

    await http.post('/projects/import_proxy', {
      title: 'New Template',
      content: 'new_template',
    })

    projects.goToBuilderAndEdit(project.slug_id, project.slug)
    return true
  }

  async function getTemplateCovers(templateId: number): Promise<TTemplateCover[] | null> {
    if (templatesCovers.value[templateId]) {
      return templatesCovers.value[templateId]
    }

    try {
      const res = await http.get<TTemplateCoverResponse>('/templates-covers/get-template-covers/' + templateId)
      if (res.status === 'success') {
        templatesCovers.value[templateId] = res.data
        return res.data
      }
    } catch {
      return null
    }

    return null
  }

  function setPageSizesForBookType(bookType: string | undefined) {
    if (bookType === 'printbook') {
      pageSizes.value = printBookPageSizes
      pageSize.value = printBookPageSizes[0]!
    } else {
      pageSizes.value = regularPageSizes
      pageSize.value = regularPageSizes[0]!
    }
  }

  async function getTemplateById(templateId: number): Promise<TTemplate | null> {
    try {
      return await http.get('/get-template-by-id/' + templateId)
    } catch {
      return null
    }
  }

  async function deleteTemplateImageElement(element: TAmazonTemplateImageElement): Promise<void> {
    await http.delete('/delete-template-image-element/' + element.id, true)
    const index = templateImageElements.value.indexOf(element)
    if (index !== -1) {
      templateImageElements.value.splice(index, 1)
    }
  }

  return {
    pageOrientations,
    pageOrientation,
    pageSizes,
    pageSize,
    ratio,
    publishTemplate,
    duplicateTemplate,
    updatePublishedTemplate,
    unpublishTemplate,
    getDefaultCCTemplateOrFirstProTemplate,
    getDefaultCCTemplateId,
    allPageSizes,
    allPrintBookPageSizes,
    regularPageSizes,
    printBookPageSizes,
    templateList,
    proTemplateLabel,
    templateToReview,
    createNewTemplate,
    getTemplateById,
    filterTemplateList,
    initTemplateList,
    getTemplateThemes,
    templatesByTags,
    deleteTemplate,
    exportTemplate,
    importTemplate,
    removeTemporaryFile,
    getTemplateCovers,
    templatesCovers,
    setPageSizesForBookType,
    fetchTemplateImageElements,
    templateImageElements,
    deleteTemplateImageElement,
  }
})
